import type { InputNumberProps } from 'antd/lib/input-number';
import React from 'react';
import { useIntl } from 'react-intl';

import { useDebouncedCallback } from 'use-debounce';

import { MAX_PRICE } from 'constants/Global';
import useNumberInputFormat from 'hooks/useNumberInputFormat';

import InputNumber from './InputNumber';
import useInputPrecision from './useInputPrecision';

const DEBOUNCED_TIME = 500;

type BoInputPriceProps = {
  currencyIsoCode?: string;
  fastType?: boolean;
  emptyValue?: number;
  value?: number | null;
  precision?: number;
} & Omit<InputNumberProps, 'precision' | 'value'>;

const InputPrice: React.FC<BoInputPriceProps> = ({ value, currencyIsoCode, emptyValue = 0, precision: precisionProp, ...rest }) => {
  const intl = useIntl();

  const precision = useInputPrecision(currencyIsoCode, precisionProp);
  const { formatter, parser: defaultParser } = useNumberInputFormat(precision);

  // Empty input falls back to emptyValue so the field renders the localized empty value (e.g. "0.00") instead of a blank
  const parser = React.useCallback(
    (displayValue?: string) => defaultParser(displayValue) || String(emptyValue),
    [defaultParser, emptyValue]
  );

  const symbolPosition = React.useMemo(() => {
    if (!currencyIsoCode) {
      return {};
    }

    const placementCurrencyParts = intl.formatNumberToParts(1, {
      style: 'currency',
      currency: currencyIsoCode,
    });

    const currencyIndex = placementCurrencyParts.findIndex(({ type }) => type === 'currency');
    const direction = currencyIndex === 0 ? 'prefix' : 'suffix';
    const currency = placementCurrencyParts[currencyIndex]?.value;

    return { [direction]: currency };
  }, [currencyIsoCode, intl]);

  return (
    <InputNumber
      min={0}
      max={MAX_PRICE}
      value={value ?? emptyValue}
      precision={precision}
      formatter={formatter}
      parser={parser}
      {...rest}
      {...symbolPosition}
    />
  );
};

type DebouncedInputPriceProps = {
  value: number | null;
  onChange: (value: number | null) => void;
  wait?: number;
} & Omit<BoInputPriceProps, 'value' | 'onChange'>;

export const DebouncedInputPrice: React.FC<DebouncedInputPriceProps> = ({ value, onChange, wait = DEBOUNCED_TIME, ...inputProps }) => {
  const debouncedOnChange = useDebouncedCallback(onChange, wait);
  const [statePrice, setStatePrice] = React.useState<number | null>(value);

  React.useEffect(() => {
    setStatePrice(value);
  }, [value]);

  const handlePriceChange = React.useCallback(
    (newValue: string | number | null) => {
      const numericValue = typeof newValue === 'string' ? parseFloat(newValue) : newValue;
      setStatePrice(numericValue ?? null);
      debouncedOnChange(numericValue ?? null);
    },
    [debouncedOnChange]
  );

  return <InputPrice {...inputProps} value={statePrice} onChange={handlePriceChange} />;
};

export default React.memo(InputPrice);
